import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Switch,
  Button,
  IconButton,
  Alert,
  Chip,
  Paper,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsIcon from "@mui/icons-material/Settings";
import LockIcon from "@mui/icons-material/Lock";
import StarIcon from "@mui/icons-material/Star";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import VerifiedIcon from "@mui/icons-material/Verified";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import EmailIcon from "@mui/icons-material/Email";
import LaunchIcon from "@mui/icons-material/Launch";
import { useUser } from "../context/UserContext";
import Toast from "./Toast";

/**
 * DailyDevotionSettingsModal Component
 * Provides complete settings for Daily Devotion inline in the Daily Devotions page:
 * 1. Default Daily Devotion Toggle (ON by default; subscription required to turn OFF)
 * 2. Mobile App Preview & Guidance
 */
const DailyDevotionSettingsModal = ({
  open,
  onClose,
  organization,
  onSettingsSaved,
}) => {
  const { user, effectiveOrgId } = useUser();
  const [devotionDefault, setDevotionDefault] = useState(true);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showSubPrompt, setShowSubPrompt] = useState(false);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const API_URL = process.env.REACT_APP_API_URL || "http://45.194.3.128:4000/api/admin/";

  // Check if current user has an active subscription or is super admin
  const isSuperAdmin = user?.role === 1;
  const isSubscribed = isSuperAdmin || user?.is_subscribed === true || user?.subscription_active === true;

  // Load existing devotion settings
  useEffect(() => {
    if (!open) return;

    const loadSettings = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem("token");
        const orgId = effectiveOrgId || (organization?.id ? organization.id : (user?.org_id !== 1 ? user?.org_id : 1));

        // Fetch devotion default setting
        const devRes = await fetch(`${API_URL}devotion-list`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ search: "", organization: orgId || null, page: 1 }),
        });
        const devData = await devRes.json();
        if (devRes.ok && devData.settings) {
          const settingVal = Array.isArray(devData.settings) ? devData.settings[0] : devData.settings;
          setDevotionDefault(settingVal?.hdailydevotiondefault !== "no");
        }
      } catch (err) {
        console.error("Error loading devotion settings:", err);
      } finally {
        setLoading(false);
      }
    };

    loadSettings();
  }, [open, organization, effectiveOrgId, user, API_URL]);

  // Handle Default Daily Devotion Toggle with subscription protection
  const handleToggleDefault = (newVal) => {
    // If trying to turn OFF without subscription
    if (!newVal && !isSubscribed) {
      setShowSubPrompt(true);
      return;
    }
    setDevotionDefault(newVal);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem("token");
      const orgId = effectiveOrgId || (organization?.id ? organization.id : (user?.org_id !== 1 ? user?.org_id : 1));

      // Update devotion settings (hdailydevotiondefault)
      const resDev = await fetch(`${API_URL}update-devotion-settings`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          devotionType: devotionDefault,
          org_id: orgId,
        }),
      });

      if (resDev.ok) {
        setToast({
          open: true,
          message: "Daily Devotion settings saved successfully!",
          severity: "success",
        });
        if (onSettingsSaved) onSettingsSaved(devotionDefault);
        setTimeout(() => {
          onClose();
        }, 800);
      } else {
        const d = await resDev.json().catch(() => ({}));
        setToast({
          open: true,
          message: d?.message || "Failed to save devotion settings",
          severity: "error",
        });
      }
    } catch (e) {
      console.error("Error saving devotion settings:", e);
      setToast({ open: true, message: "Network error saving settings", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            background: "linear-gradient(135deg, #112229 0%, #0d1a20 100%)",
            color: "#fff",
            borderRadius: 3,
            border: "1px solid rgba(76, 161, 175, 0.4)",
            boxShadow: "0 20px 40px rgba(0,0,0,0.6)",
          },
        }}
      >
        {/* Header */}
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pb: 1.5,
            borderBottom: "1px solid rgba(76, 161, 175, 0.2)",
          }}
        >
          <Box display="flex" alignItems="center" gap={1.5}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: 2,
                backgroundColor: "rgba(76, 161, 175, 0.2)",
                border: "1px solid rgba(76, 161, 175, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#4CA1AF",
              }}
            >
              <SettingsIcon sx={{ fontSize: 22 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.1rem", color: "#fff" }}>
                Daily Devotion Settings
              </Typography>
              <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                Configure default feed behavior
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)", "&:hover": { color: "#fff" } }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3 }}>
          {/* Section 1: Default Daily Devotion Setting */}
          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2.5,
              background: "rgba(23, 115, 115, 0.12)",
              border: "1px solid rgba(76, 161, 175, 0.3)",
            }}
          >
            <Box display="flex" alignItems="flex-start" justifyContent="space-between" gap={2}>
              <Box>
                <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                  <MenuBookIcon sx={{ fontSize: 18, color: "#4CA1AF" }} />
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#fff" }}>
                    Default Daily Devotion
                  </Typography>
                  <Chip
                    size="small"
                    label={devotionDefault ? "ON (Default)" : "OFF (Custom Only)"}
                    color={devotionDefault ? "success" : "default"}
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      backgroundColor: devotionDefault ? "rgba(46, 125, 50, 0.3)" : "rgba(255, 255, 255, 0.1)",
                      color: devotionDefault ? "#81c784" : "rgba(255,255,255,0.7)",
                      border: "1px solid",
                      borderColor: devotionDefault ? "rgba(46, 125, 50, 0.5)" : "rgba(255, 255, 255, 0.2)",
                    }}
                  />
                </Box>
                <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.85rem", mb: 1 }}>
                  When <strong>ON</strong>, all app users receive the curated global daily devotion (e.g., Kumara Krupa).
                  When <strong>OFF</strong>, only custom organization-created devotions will appear.
                </Typography>
                <Box display="flex" alignItems="center" gap={0.8}>
                  <LockIcon sx={{ fontSize: 14, color: "#e0a96d" }} />
                  <Typography variant="caption" sx={{ color: "#e0a96d", fontSize: "0.75rem", fontWeight: 600 }}>
                    Subscription required to turn OFF default devotions
                  </Typography>
                </Box>
              </Box>

              <Switch
                checked={devotionDefault}
                onChange={(e) => handleToggleDefault(e.target.checked)}
                disabled={loading}
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#4CA1AF",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#177373",
                  },
                }}
              />
            </Box>

            {/* Subscribed Badge or Upgrade Notice */}
            {isSubscribed ? (
              <Box
                mt={2}
                pt={1.5}
                borderTop="1px dashed rgba(76, 161, 175, 0.25)"
                display="flex"
                alignItems="center"
                gap={1}
              >
                <VerifiedIcon sx={{ fontSize: 16, color: "#64d2e2" }} />
                <Typography variant="caption" sx={{ color: "#64d2e2", fontWeight: 600 }}>
                  Active Subscription detected — You are authorized to customize this setting.
                </Typography>
              </Box>
            ) : (
              <Box
                mt={2}
                pt={1.5}
                borderTop="1px dashed rgba(76, 161, 175, 0.25)"
                display="flex"
                alignItems="center"
                justifyContent="space-between"
              >
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.6)" }}>
                  Free plan users have default daily devotion locked ON.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<StarIcon sx={{ fontSize: 14 }} />}
                  onClick={() => setShowSubPrompt(true)}
                  sx={{
                    fontSize: "0.72rem",
                    py: 0.3,
                    px: 1.2,
                    textTransform: "none",
                    borderColor: "rgba(224, 169, 109, 0.5)",
                    color: "#e0a96d",
                    "&:hover": { borderColor: "#e0a96d", backgroundColor: "rgba(224, 169, 109, 0.1)" },
                  }}
                >
                  Upgrade
                </Button>
              </Box>
            )}
          </Paper>

          {/* Subscription Modal / Notice for Free Users */}
          {showSubPrompt && (
            <Alert
              severity="warning"
              onClose={() => setShowSubPrompt(false)}
              sx={{
                mt: 2.5,
                backgroundColor: "rgba(224, 169, 109, 0.15)",
                border: "1px solid rgba(224, 169, 109, 0.4)",
                color: "#ffecb3",
                "& .MuiAlert-icon": { color: "#e0a96d" },
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#fff", mb: 0.5 }}>
                ⭐ Subscription Required
              </Typography>
              <Typography variant="body2" sx={{ fontSize: "0.82rem", color: "rgba(255,255,255,0.85)", mb: 1.5 }}>
                Free tier organizations can only provide the default curated Daily Devotions.
                To turn off default devotions and exclusively stream your organization's custom meditations, please upgrade to a Premium plan.
              </Typography>
              <Box display="flex" gap={1}>
                <Button
                  size="small"
                  variant="contained"
                  onClick={() => setContactModalOpen(true)}
                  sx={{
                    backgroundColor: "#e0a96d",
                    color: "#0d1a20",
                    fontWeight: 700,
                    textTransform: "none",
                    "&:hover": { backgroundColor: "#c89456" },
                  }}
                >
                  View Subscription Plans
                </Button>
                <Button
                  size="small"
                  variant="text"
                  onClick={() => setShowSubPrompt(false)}
                  sx={{ color: "rgba(255,255,255,0.7)", textTransform: "none" }}
                >
                  Keep Default ON
                </Button>
              </Box>
            </Alert>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid rgba(76, 161, 175, 0.2)",
            display: "flex",
            justifyContent: "space-between",
          }}
        >
          <Button onClick={onClose} sx={{ color: "rgba(255,255,255,0.6)", textTransform: "none" }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{
              background: "linear-gradient(135deg, #177373 0%, #256974 100%)",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              borderRadius: 2,
              "&:hover": {
                background: "linear-gradient(135deg, #1C8A8A 0%, #2f7e8a 100%)",
              },
            }}
          >
            {saving ? "Saving..." : "Save Settings"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact Glocal Prayer Network Team Modal */}
      <Dialog
        open={contactModalOpen}
        onClose={() => setContactModalOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: "#0d1a20",
            backgroundImage: "linear-gradient(135deg, rgba(13, 26, 32, 0.98) 0%, rgba(20, 42, 50, 0.98) 100%)",
            border: "1px solid rgba(76, 161, 175, 0.35)",
            borderRadius: 3,
            boxShadow: "0 24px 48px rgba(0, 0, 0, 0.6), 0 0 30px rgba(76, 161, 175, 0.15)",
            color: "#FFFFFF",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pb: 1,
            pt: 2,
            px: 2.5,
          }}
        >
          <Box display="flex" alignItems="center" gap={1.2}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: "linear-gradient(135deg, rgba(224, 169, 109, 0.3) 0%, rgba(224, 169, 109, 0.1) 100%)",
                border: "1px solid rgba(224, 169, 109, 0.4)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <SupportAgentIcon sx={{ color: "#e0a96d", fontSize: 22 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 700, fontSize: "1.05rem", color: "#FFFFFF" }}>
              Subscription Plans
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setContactModalOpen(false)}
            sx={{
              color: "rgba(255,255,255,0.6)",
              "&:hover": { color: "#FFFFFF", backgroundColor: "rgba(255,255,255,0.08)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2 }}>
          <Box
            sx={{
              p: 2,
              borderRadius: 2,
              backgroundColor: "rgba(224, 169, 109, 0.08)",
              border: "1px solid rgba(224, 169, 109, 0.25)",
              mb: 2.5,
            }}
          >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: "#e0a96d", mb: 0.75 }}>
              ⭐ No Online Plan Available Yet
            </Typography>
            <Typography variant="body2" sx={{ fontSize: "0.85rem", color: "rgba(255,255,255,0.88)", lineHeight: 1.6 }}>
              Currently, there is no direct online subscription plan. Please contact the <strong>Glocal Prayer Network</strong> team to upgrade your organization and exclusively stream your custom Daily Devotions.
            </Typography>
          </Box>

          <Typography
            variant="caption"
            sx={{
              color: "rgba(255,255,255,0.6)",
              textTransform: "uppercase",
              fontWeight: 700,
              letterSpacing: 0.5,
              display: "block",
              mb: 1.5,
            }}
          >
            Please contact our team:
          </Typography>

          <Box display="flex" flexDirection="column" gap={1.25}>
            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.2}>
                <EmailIcon sx={{ color: "#64d2e2", fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", fontSize: "0.72rem" }}>
                    Email Support
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#FFFFFF", fontSize: "0.86rem" }}>
                    support@glocalprayer.net
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                component="a"
                href="mailto:support@glocalprayer.net?subject=Daily%20Devotion%20Subscription%20Inquiry"
                sx={{
                  color: "#64d2e2",
                  borderColor: "rgba(76, 161, 175, 0.4)",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  py: 0.4,
                  px: 1.2,
                  "&:hover": {
                    borderColor: "#64d2e2",
                    backgroundColor: "rgba(76, 161, 175, 0.15)",
                  },
                }}
              >
                Send Email
              </Button>
            </Box>

            <Box
              sx={{
                p: 1.5,
                borderRadius: 2,
                backgroundColor: "rgba(255,255,255,0.04)",
                border: "1px solid rgba(255,255,255,0.08)",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box display="flex" alignItems="center" gap={1.2}>
                <LaunchIcon sx={{ color: "#e0a96d", fontSize: 20 }} />
                <Box>
                  <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.5)", display: "block", fontSize: "0.72rem" }}>
                    Online Portal
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: "#FFFFFF", fontSize: "0.86rem" }}>
                    glocalprayer.net/contact-us
                  </Typography>
                </Box>
              </Box>
              <Button
                size="small"
                variant="outlined"
                component="a"
                href="https://glocalprayer.net/contact-us/"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "#e0a96d",
                  borderColor: "rgba(224, 169, 109, 0.4)",
                  textTransform: "none",
                  fontSize: "0.75rem",
                  py: 0.4,
                  px: 1.2,
                  "&:hover": {
                    borderColor: "#e0a96d",
                    backgroundColor: "rgba(224, 169, 109, 0.15)",
                  },
                }}
              >
                Visit
              </Button>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, pb: 2, pt: 1 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={() => setContactModalOpen(false)}
            sx={{
              background: "linear-gradient(135deg, #177373 0%, #256974 100%)",
              color: "#FFFFFF",
              textTransform: "none",
              fontWeight: 600,
              borderRadius: 2,
              py: 0.8,
              "&:hover": {
                background: "linear-gradient(135deg, #1C8A8A 0%, #2f7e8a 100%)",
              },
            }}
          >
            Got It
          </Button>
        </DialogActions>
      </Dialog>

      <Toast toast={toast} setToast={setToast} />
    </>
  );
};

export default DailyDevotionSettingsModal;
